import { ErrorResponse, Member, MemberList, MembersApi } from '@citruscamps/citrus-client'
import { Dispatch, useState } from 'react'
import {
  FetchNextPageOptions,
  InfiniteQueryObserverResult,
  useInfiniteQuery,
  useQuery,
} from 'react-query'
import { DefaultMainPagination } from '../../../../../constants/pagination'
import { useLocalStorage } from '../../../../../hooks/useLocalStorage'
import { useRequestHandler } from '../../../../../hooks/useRequestHandler'
import { useRouter } from '../../../../../hooks/useRouter'
import { useSearchValue } from '../../../../../hooks/useSearchValue'
import { Pagination } from '../../../../../interfaces/pagination'
import { generateApiConfig } from '../../../../../utils/client-config'
import { generateListKey } from '../../../../../utils/key-generator'
import { useHandleMembers } from './useHandleMembers'

export const getMemberQueryValues = (
  filter: MemberFilterProps,
): Omit<MemberFilterProps, 'is_inactive'> => {
  const is_active = !!filter?.is_active !== !!filter?.is_inactive ? filter?.is_active : undefined
  const pending_invite = !(
    filter?.is_active === !!filter?.is_inactive && filter?.is_active === filter?.pending_invite
  )
    ? !!filter?.pending_invite
    : undefined
  return { is_active, pending_invite }
}

const DefaultFetchProps: FetchProps = {
  sort: 'first_name',
  order: 'ASC',
  filter: {
    is_active: false,
    is_inactive: false,
    pending_invite: false,
  },
}

export interface MemberFilterProps {
  is_active?: boolean
  is_inactive?: boolean
  pending_invite?: boolean
}

interface FetchProps {
  sort?: string
  order?: 'ASC' | 'DESC'
  filter: MemberFilterProps
}

interface IProps {
  programId?: string
  pagination?: Pagination
  fetchProps?: FetchProps
  overwriteFilters?: boolean
  fetchAll?: boolean
}

interface FetchMembers {
  data: Member[]
  error: ErrorResponse | null
  fetchProps: FetchProps
  isError: boolean
  isLoading: boolean
  pagination: Pagination
  search: string
  handleActivate: (values: Member) => Promise<void>
  handleDeactivate: (values: Member) => Promise<void>
  handleResendInvite: (values: Member) => Promise<void>
  handleRevokeInvite: (values: Member) => Promise<void>
  setFetchProps: (value?: FetchProps) => void
  setPagination: Dispatch<Pagination>
  setSearch: Dispatch<string>
}

export const useFetchMembers = ({
  programId,
  pagination: initialPagination,
  overwriteFilters,
  ...props
}: IProps): FetchMembers => {
  const { requestHandler } = useRequestHandler()
  const { pathname } = useRouter()
  const initialFetchProps: FetchProps = props.fetchProps || DefaultFetchProps
  const [pagination, setPagination] = useState<Pagination>(
    initialPagination || DefaultMainPagination,
  )
  const [fetchProps, setFetchProps] = useLocalStorage<FetchProps>(
    `member-filter-${pathname}`,
    initialFetchProps,
    overwriteFilters,
  )
  const { search, deferSearch, setSearch, isSearching } = useSearchValue('', {
    onDefer: () => setPagination(initialPagination || DefaultMainPagination),
  })
  const queryKeys = generateListKey({
    type: 'member',
    program_id: programId,
    pagination,
    sort: fetchProps.sort,
    order: fetchProps.order,
    query: fetchProps.filter,
    search: deferSearch,
  })
  const {
    isLoading,
    isError,
    data: resp,
    error,
  } = useQuery<MemberList, ErrorResponse>(
    queryKeys,
    async ({ signal }) => {
      if (!programId) {
        throw new Error('Unable to fetch data')
      }
      const client = new MembersApi(generateApiConfig())
      const skip = pagination.limit ? pagination.page * pagination.limit : undefined
      const { is_active, pending_invite } = getMemberQueryValues(fetchProps.filter)
      const resp: MemberList = await requestHandler<MemberList>(() =>
        client.findMembers(
          {
            program_id: programId,
            limit: pagination.limit,
            skip,
            order: fetchProps.order || undefined,
            sort: fetchProps.sort || undefined,
            search: search || undefined,
            is_active,
            pending_invite,
          },
          { signal },
        ),
      )
      return resp
    },
    { enabled: !!programId },
  )

  const { handleActivate, handleDeactivate, handleResendInvite, handleRevokeInvite } =
    useHandleMembers({ programId })

  const _setFetchProps = (val?: FetchProps) => {
    setPagination(initialPagination || DefaultMainPagination)
    return setFetchProps(val || DefaultFetchProps)
  }

  return {
    data: resp?.data || [],
    error,
    fetchProps,
    isError,
    isLoading: isLoading || isSearching,
    pagination: {
      ...pagination,
      count: resp?.count || pagination.count,
      total: resp?.total || pagination.total,
      page: resp?.page || pagination.page,
      page_count: resp?.page_count || pagination.page_count,
    },
    search,
    handleActivate,
    handleDeactivate,
    handleResendInvite,
    handleRevokeInvite,
    setFetchProps: _setFetchProps,
    setPagination,
    setSearch,
  }
}

interface FetchInfiniteMembers extends Omit<FetchMembers, 'setPagination'> {
  isFetching: boolean
  isFetchingNextPage: boolean
  fetchNextPage: (
    options?: FetchNextPageOptions | undefined,
  ) => Promise<InfiniteQueryObserverResult<MemberList, ErrorResponse>>
  hasNextPage: boolean
}

export const useFetchInfiniteMembers = ({
  programId,
  pagination: initialPagination,
  overwriteFilters,
  fetchAll = false,
  ...props
}: IProps): FetchInfiniteMembers => {
  const { requestHandler } = useRequestHandler()
  const { pathname } = useRouter()
  const initialFetchProps: FetchProps = props.fetchProps || DefaultFetchProps
  const [pagination, setPagination] = useState<Pagination>(
    initialPagination || DefaultMainPagination,
  )
  const [fetchProps, setFetchProps] = useLocalStorage<FetchProps>(
    `member-filter-${pathname}`,
    initialFetchProps,
    overwriteFilters,
  )
  const { search, deferSearch, setSearch, isSearching } = useSearchValue('', {
    onDefer: () => setPagination(initialPagination || DefaultMainPagination),
  })
  const queryKeys = generateListKey({
    type: 'member',
    program_id: programId,
    pagination,
    sort: fetchProps.sort,
    order: fetchProps.order,
    query: fetchProps.filter,
    search: deferSearch,
  })
  const {
    data: resp,
    error,
    fetchNextPage,
    hasNextPage = false,
    isError,
    isFetching,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery<MemberList, ErrorResponse>(
    queryKeys,
    async ({ signal, pageParam: pg = pagination }) => {
      if (!programId) {
        throw new Error('Unable to fetch data')
      }
      const client = new MembersApi(generateApiConfig())
      const skip = pg.limit ? pg.page * pg.limit : undefined
      const { is_active, pending_invite } = getMemberQueryValues(fetchProps.filter)
      const resp: MemberList = await requestHandler<MemberList>(() =>
        client.findMembers(
          {
            program_id: programId,
            limit: pg.limit,
            skip,
            order: fetchProps.order || undefined,
            sort: fetchProps.sort || undefined,
            search: search || undefined,
            is_active,
            pending_invite,
          },
          { signal },
        ),
      )
      return resp
    },
    {
      getNextPageParam: (resp: MemberList): Pagination | undefined => {
        if (!resp.page_count || resp.page < resp.page_count - 1) {
          return {
            ...pagination,
            count: resp?.count,
            total: resp?.total,
            page: resp?.page + 1,
            page_count: resp?.page_count,
          }
        }
        return undefined
      },
      getPreviousPageParam: (resp: MemberList): Pagination | undefined => {
        if (!resp.page_count || resp.page <= resp.page_count) {
          return {
            ...pagination,
            count: resp?.count,
            total: resp?.total,
            page: resp?.page - 1,
            page_count: resp?.page_count,
          }
        }
        return undefined
      },
      enabled: !!programId,
    },
  )
  const { handleActivate, handleDeactivate, handleResendInvite, handleRevokeInvite } =
    useHandleMembers({ programId })
  const _setFetchProps = (val?: FetchProps) => {
    return setFetchProps(val || DefaultFetchProps)
  }

  if (!(isFetching || isFetchingNextPage || isLoading) && hasNextPage && fetchAll) {
    fetchNextPage()
  }
  const response = [...(resp?.pages || [])].pop()

  return {
    data: (resp?.pages || []).reduce<Member[]>((list, p) => [...list, ...p.data], []) || [],
    error,
    fetchProps,
    isError,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    isLoading: isLoading || isSearching,
    pagination: {
      ...pagination,
      count: response?.count || pagination.count,
      total: response?.total || pagination.total,
      page: response?.page || pagination.page,
      page_count: response?.page_count || pagination.page_count,
    },
    search,
    handleActivate,
    handleDeactivate,
    handleResendInvite,
    handleRevokeInvite,
    setFetchProps: _setFetchProps,
    setSearch,
    fetchNextPage,
  }
}
